import { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronLeft, ChevronRight, Sparkles, X } from 'lucide-react';
import { newsCover, newsPagePath } from '../../constants/news.constants';
import { useDialogA11y } from '../../hooks/useDialogA11y';
import { useRecordNewsEvents } from '../../hooks/useNewsReader';
import type { NewsEventInput, NewsItem, NewsPlatform } from '../../types/news.types';
import { formatNewsDate } from '../../utils/news.utils';
import { NewsCtaLink } from './NewsCtaLink';

const SECONDARY_BUTTON =
  'tw-inline-flex tw-items-center tw-gap-1 tw-px-3 tw-py-2 tw-text-sm tw-font-medium tw-text-gray-700 tw-bg-white tw-border tw-border-solid tw-border-gray-300 tw-rounded-lg hover:tw-bg-gray-50 disabled:tw-opacity-40 disabled:tw-cursor-not-allowed tw-transition-colors';
const PRIMARY_BUTTON =
  'tw-inline-flex tw-items-center tw-gap-1 tw-px-3 tw-py-2 tw-text-sm tw-font-medium tw-text-white tw-bg-affinicar-primary tw-border tw-border-solid tw-border-affinicar-primary tw-rounded-lg hover:tw-bg-affinicar-secondary tw-transition-colors';

/**
 * Modale d'arrivée : un slider des nouveautés à présenter (3 au plus, fournies
 * par l'API). Montée seulement quand elle doit s'afficher ; `slides` est figé à
 * l'ouverture par le parent.
 *
 * Suivi — sémantique décidée avec le produit :
 * - à l'ouverture, UN appel : `modal_delivered` pour toutes les diapos (la modale
 *   ne reviendra pas) et `modal_viewed` pour la première, la seule affichée ;
 * - `modal_viewed` pour une autre diapo seulement quand l'utilisateur y arrive.
 *   Fermer après la première laisse les suivantes non lues : elles restent dans
 *   le fil et dans le badge.
 *
 * Fermeture : croix, bouton, Échap. PAS le clic sur le fond — une annonce ne
 * doit pas être consommée par un clic à côté.
 */
export function NewsArrivalModal({
  platform,
  slides,
  onClose,
}: {
  platform: NewsPlatform;
  slides: NewsItem[];
  onClose: () => void;
}) {
  const [index, setIndex] = useState(0);
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const recordEvents = useRecordNewsEvents(platform);
  /** Diapos déjà déclarées vues : chacune ne l'est qu'une fois. */
  const viewedIds = useRef(new Set<string>());
  const openingDeclared = useRef(false);

  const count = slides.length;
  const isFirst = index === 0;
  const isLast = index === count - 1;

  const goTo = (target: number) => {
    if (target < 0 || target >= count || target === index) return;
    setIndex(target);
    const { id } = slides[target];
    if (!viewedIds.current.has(id)) {
      viewedIds.current.add(id);
      recordEvents([{ newsId: id, types: ['modal_viewed'] }]);
    }
  };

  // Effet pur : déclarer l'ouverture, une seule fois (la référence protège du
  // double passage du mode strict). `slides` et `recordEvents` sont stables.
  useEffect(() => {
    if (openingDeclared.current || slides.length === 0) return;
    openingDeclared.current = true;
    viewedIds.current.add(slides[0].id);
    recordEvents(
      slides.map(
        (slide, position): NewsEventInput => ({
          newsId: slide.id,
          types: position === 0 ? ['modal_delivered', 'modal_viewed'] : ['modal_delivered'],
        }),
      ),
    );
  }, [slides, recordEvents]);

  useDialogA11y(panelRef, {
    onEscape: onClose,
    onArrowLeft: () => goTo(index - 1),
    onArrowRight: () => goTo(index + 1),
  });

  const slide = slides[index];
  if (!slide) return null;
  const publishedOn = formatNewsDate(slide.publishedAt);
  const slideCover = newsCover(slide);

  return createPortal(
    <div className="tw-fixed tw-inset-0 tw-z-[9990] tw-bg-black/60 tw-flex tw-items-center tw-justify-center tw-p-4">
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className="tw-bg-white tw-rounded-xl tw-shadow-2xl tw-w-full tw-max-w-lg tw-max-h-[90vh] tw-flex tw-flex-col tw-overflow-hidden tw-outline-none tw-text-left"
      >
        <div className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-px-5 tw-py-3 tw-border-b tw-border-gray-200 tw-flex-shrink-0">
          <span className="tw-inline-flex tw-items-center tw-gap-2 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-affinicar-secondary">
            <Sparkles className="tw-w-4 tw-h-4" aria-hidden="true" />
            {count > 1 ? `Nouveautés · ${index + 1} sur ${count}` : 'Nouveauté'}
          </span>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fermer"
            className="tw-inline-flex tw-items-center tw-justify-center tw-w-8 tw-h-8 tw-rounded-lg tw-border-0 tw-bg-transparent tw-text-gray-500 hover:tw-bg-gray-100 hover:tw-text-gray-800 tw-transition-colors"
          >
            <X className="tw-w-5 tw-h-5" />
          </button>
        </div>

        <div className="tw-flex-1 tw-min-h-0 tw-overflow-y-auto" aria-live="polite">
          {slideCover.url && (
            <img
              key={slide.id}
              src={slideCover.url}
              alt=""
              className={`tw-block tw-w-full ${
                slideCover.contain ? 'tw-h-auto tw-bg-white' : 'tw-aspect-video tw-object-cover tw-bg-gray-100'
              }`}
            />
          )}
          <div className="tw-px-5 tw-py-4">
            {publishedOn && <p className="tw-text-xs tw-text-gray-500 tw-mb-1">{publishedOn}</p>}
            <h2 id={titleId} className="tw-text-xl tw-font-bold tw-text-gray-900 tw-mt-0 tw-mb-2 tw-break-words">
              {slide.title}
            </h2>
            {slide.shortDescription && (
              <p className="tw-text-sm tw-text-gray-600 tw-mb-0 tw-whitespace-pre-line tw-break-words">
                {slide.shortDescription}
              </p>
            )}
            <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-4 tw-gap-y-2 tw-mt-4">
              <NewsCtaLink
                platform={platform}
                news={slide}
                onInternalNavigation={onClose}
                className={`${PRIMARY_BUTTON} tw-no-underline hover:tw-text-white`}
              >
                <ArrowRight className="tw-w-4 tw-h-4" aria-hidden="true" />
              </NewsCtaLink>
              <Link
                to={newsPagePath(slide.slug)}
                onClick={onClose}
                className="tw-text-sm tw-font-medium tw-text-affinicar-secondary hover:tw-text-affinicar-primary tw-underline"
              >
                En savoir plus
              </Link>
            </div>
          </div>
        </div>

        <div className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-px-5 tw-py-3 tw-border-t tw-border-gray-200 tw-flex-shrink-0">
          {count > 1 ? (
            <>
              <button type="button" onClick={() => goTo(index - 1)} disabled={isFirst} className={SECONDARY_BUTTON}>
                <ChevronLeft className="tw-w-4 tw-h-4" aria-hidden="true" />
                Précédent
              </button>

              <div className="tw-flex tw-items-center tw-gap-1">
                {slides.map((item, position) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => goTo(position)}
                    aria-label={`Afficher la nouveauté ${position + 1} sur ${count}`}
                    aria-current={position === index ? 'true' : undefined}
                    className="tw-inline-flex tw-items-center tw-justify-center tw-w-5 tw-h-5 tw-p-0 tw-border-0 tw-bg-transparent"
                  >
                    <span
                      className={`tw-block tw-h-2 tw-rounded-full tw-transition-all ${
                        position === index ? 'tw-w-4 tw-bg-affinicar-secondary' : 'tw-w-2 tw-bg-gray-300'
                      }`}
                    />
                  </button>
                ))}
              </div>

              {isLast ? (
                <button type="button" onClick={onClose} className={PRIMARY_BUTTON}>
                  Fermer
                </button>
              ) : (
                <button type="button" onClick={() => goTo(index + 1)} className={PRIMARY_BUTTON}>
                  Suivant
                  <ChevronRight className="tw-w-4 tw-h-4" aria-hidden="true" />
                </button>
              )}
            </>
          ) : (
            <button type="button" onClick={onClose} className={`${PRIMARY_BUTTON} tw-ml-auto`}>
              Fermer
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
